<template>
  <button
    ref="resultEl"
    :class="['flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1', { 'bg-bgr-secondary': isActive }]"
    :data-result="`${props.result.package_number}_${props.result.id}`"
    @click="onClickWrapper(onClick, $event)"
  >
    <div class="flex w-full items-center gap-2.5">
      <Avatar
        :size="ComponentSize.lg"
        :entity="{
          name: props.result.carrier,
          image: getCarrierLogoUrl(props.result.carrier),
        }"
      />
      <div class="flex flex-col items-start">
        <div class="text-body-large flex items-center font-medium">
          <p class="max-w-xs truncate">
            <span v-for="(part, i) in title" :key="part" :class="{ 'bg-bgr-warning-secondary': i === 1 }">{{
              part
            }}</span>
          </p>
          <!-- not links: the result is already a button -->
          <Tooltip v-for="ticket in ticketIds" :key="ticket" :text="ticketLabel(ticket)">
            <Icon
              src="bold/TagSimple"
              filled
              role="img"
              class="text-fgr-critical ms-1"
              :aria-label="ticketLabel(ticket)"
            />
          </Tooltip>
          <span class="ms-2">–</span>
          <span class="text-body-default ms-2 text-nowrap">{{ matchedAttibute }}</span>
        </div>
        <div class="text-body-small w-fit">
          <span>{{ type }} / </span>
          <span
            v-for="(part, i) in highlightSearch(props.result.package_number, gsStore.search)"
            :key="part"
            :class="i === 1 ? 'bg-bgr-warning-secondary' : 'text-fgr-primary'"
            >{{ part }}</span
          >
        </div>
      </div>
      <div :class="['ms-auto items-center gap-1 p-2', isActive ? 'flex' : 'hidden']">
        <span class="text-body-small-m">{{ t("select") }}</span>
        <Icon src="ArrowUDownLeft" />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
  import type { SearchResultCategory, SearchResultItem } from "~/types/search";

  defineOptions({
    name: "GlobalSearchResult",
  });

  const props = withDefaults(
    defineProps<{
      category?: SearchResultCategory;
      icon?: string | false;
      isPrevious?: boolean;
      result: SearchResultItem;
    }>(),
    {
      icon: "ClockCounterClockwise",
      category: undefined,
    }
  );
  const emit = defineEmits(["close"]);

  const { t } = useI18n();
  const gsStore = useGlobalSearchStore();
  const router = useRouter();

  const resultEl = useTemplateRef("resultEl");

  const title = computed(() => {
    return highlightSearch(highlightAttributeStringValue(props.result), gsStore.search);
  });

  const type = computed(() => {
    return globalSearchResultType(props.result.type);
  });

  const matchedAttibute = computed(() => {
    return globalSearchResultTitleAttribute(props.result);
  });

  const isActive = computed(() => {
    return gsStore.currentResult?.id === props.result.id;
  });

  const onClick = computed(() => {
    return globalSearchResultOnClick(props.result);
  });

  watch(isActive, (isActive) => {
    if (isActive && gsStore.usingKeyboard) {
      resultEl.value?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  });

  const onClickWrapper = (oc: Record<string, any> | ((...args: any[]) => any), e: MouseEvent): void => {
    addResultToRecentSearches(props.result);
    emit("close");

    if (typeof oc === "function") {
      return oc();
    } else {
      const resolved = router.resolve(oc);
      if (e.metaKey || e.ctrlKey) {
        return window.open(resolved.href, "_blank");
      }

      return useRouter().push(oc);
    }
  };

  // a shipment in a bulk case shows only the case
  const isBulk = computed(() => !!props.result.parent_tickets?.length);
  const ticketIds = computed(() => (isBulk.value ? props.result.parent_tickets : props.result.tickets));
  const ticketLabel = (id: number | string) =>
    isBulk.value ? t("bulk_tickets.parent_ticket", { id }) : t("ticket_number") + " #" + id;
</script>
